// src/hooks/useAxios.js
import { useMemo } from 'react'
import axios from 'axios'
import { toastrError } from '@elementComponents/Toastr'
import { useSafeAccessToken } from './useSafeAccessToken'

const useAxios = () => {
  const getToken = useSafeAccessToken()

  const apiClient = useMemo(() => {
    const client = axios.create({
      baseURL: import.meta.env.VITE_NEW_USER_API,
      withCredentials: true,
    })

    client.interceptors.request.use(async (config) => {
      try {
        const token = await getToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config
      } catch (e) {
        // If token acquisition fails, let the response interceptor handle redirects
        return Promise.reject(e)
      }
    })

    client.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error

        if (response?.status === 500) {
          const { title, details } = response.data || {}
          const message = details || title || 'Internal server error'
          toastrError('🔥 Server error (500):', message)
          console.error('🔥 Server error (500):', message)
        }

        if (response?.status === 403 || response?.status === 401) {
          if (response.data?.errors) {
            const validationErrors = response.data.errors
            const messages = Object.values(validationErrors).flat()
            messages.forEach((msg) => toastrError('❌ Forbidden', msg))
            return Promise.reject({ status: response?.status, messages })
          }

          return Promise.reject(error)
        }

        if (response?.status === 404 && response.data?.errors) {
          const validationErrors = response.data.errors
          const messages = Object.values(validationErrors).flat()
          messages.forEach((msg) => toastrError('❌ Validation Error:', msg))
          return Promise.reject({ status: 404, messages })
        }

        if (response?.status === 409 && response.data?.errors) {
          const conflictErrors = response.data.errors
          const messages = Object.values(conflictErrors).flat()
          messages.forEach((msg) => toastrError('❌ Conflict Error:', msg))
          return Promise.reject({ status: 409, messages })
        }
        return Promise.reject(error)
      }
    )

    return client
  }, [getToken])

  return apiClient
}

export default useAxios
